import RouteTemplate from 'ember-route-template'
import { service } from '@ember/service'
import BaseRouteTemplate from 'customer-flows/templates/base-route-template'
import TwoColumn from 'customer-flows/components/layout-kit/two-column'
import StepContainer from 'customer-flows/components/flow-kit/step-container'
import HeroContainer from 'customer-flows/components/flow-kit/hero-container'
import ProgressBar from 'customer-flows/components/flow-kit/progress-bar'
import StaticFooter from 'customer-flows/components/booking/static-footer'
import StickyFooter from 'customer-flows/components/booking/sticky-footer'
import CartSummary from 'customer-flows/components/booking/cart-summary'
import CardIssuerDiscount from 'customer-flows/components/payments/card-issuer-discount'
import CustomerInfoSummary from 'customer-flows/components/booking/customer-info-summary'
import Icon from 'customer-flows/components/ui-kit/icon'
import t from 'ember-intl/helpers/t'
import AnimatedContainer from 'ember-animated/components/animated-container'
import animatedIf from 'ember-animated/components/animated-if'
import animateHeight from 'customer-flows/utils/animations/animate-height'
import { and, not, or } from 'ember-truth-helpers'
import { action } from '@ember/object'
import { on } from '@ember/modifier'
import { command, context } from 'customer-flows/utils/core'
import Logger from 'customer-flows/utils/logger'
import { tracked } from '@glimmer/tracking'
import { later } from '@ember/runloop'
import { IliadThemeHandler } from '@spinnaker/iliad/dist/utils/iliad-theme-handler'
import { signal } from 'customer-flows/utils/core'
import fixSafariTooltip from 'customer-flows/modifiers/fix-safari-tooltip'
import { KLARNA_PAYMENT_METHOD_TYPE } from 'customer-flows/utils/stripe-payment-method-types'

const progressBarExcludedPages: PageType[] = ['FlowConfigBookingStartPage']

export const PaymentAttemptIssue = signal<{
  reason: 'missing-required-fields'
}>()

const PAYMENT_ERROR_MESSAGE_KEYS: Partial<Record<CreateOrderFromQuoteErrorCode, string>> = {
  PAYMENT_DECLINED: 'booking.payment.error.payment-declined',
  CARD_ISSUER_DISCOUNT_INELIGIBLE: 'booking.payment.card-issuer-discount.ineligible',
  NO_LONGER_AVAILABLE: 'booking.payment.error.no-longer-available',
  SELECTED_RESOURCE_AVAILABLE: 'booking.payment.error.no-longer-available',
  ALREADY_PROCESSED: 'booking.payment.error.already-processed',
  KLARNA_CANCELLED: 'booking.payment.error.klarna-cancelled',
  KLARNA_DECLINED: 'booking.payment.error.klarna-declined',
  KLARNA_SOLD_OUT_REFUNDED: 'booking.payment.error.klarna-sold-out-refunded',
  KLARNA_REFUND_IN_FLIGHT: 'booking.payment.error.klarna-refund-in-flight'
}

class BookingPaymentTemplate extends BaseRouteTemplate<BookingPaymentPage> {
  @service eventTracker: EventTracker

  @command('payment') buildOrderInput: PaymentCommands['buildOrderInput']
  @command('payment') createOrdersFromQuotes: PaymentCommands['createOrdersFromQuotes']
  @command('payment') payWithKlarna: PaymentCommands['payWithKlarna']
  @command('payment') setPaymentFormLoading: PaymentCommands['setPaymentFormLoading']
  @command('payment') setOrderCreationLoading: PaymentCommands['setOrderCreationLoading']
  @command('payment') setPaymentErrorCode: PaymentCommands['setPaymentErrorCode']
  @command('payment') teardownKlarnaAttempt: PaymentCommands['teardownKlarnaAttempt']

  @context('payment') paymentFormIsValid: PaymentStore['paymentFormIsValid']
  @context('payment') paymentFormIsEmpty: PaymentStore['paymentFormIsEmpty']
  @context('payment') paymentFormIsLoading: PaymentStore['paymentFormIsLoading']
  @context('payment') isCreatingOrder: PaymentStore['isCreatingOrder']
  @context('payment') paymentMethod: PaymentStore['paymentMethod']
  @context('payment') selectedPaymentMethodType: PaymentStore['selectedPaymentMethodType']
  @context('payment') paymentErrorCode: PaymentStore['paymentErrorCode']
  @context('payment') hasSuccessfulPayment: PaymentStore['hasSuccessfulPayment']
  @context('quote') isFree: QuoteStore['isFree']
  @context('quote') cardIssuerDiscountRedeemable: QuoteStore['cardIssuerDiscountRedeemable']
  @context('quote') quoteExpirationTimeInSeconds: QuoteStore['quoteExpirationTimeInSeconds']

  @tracked attemptedSubmitWithEmptyFields = false

  animateHeight = animateHeight

  // useful to prevent ghost-clicking on safari mobile
  private _constructedAt: number = Date.now()

  constructor(owner: unknown, args: any) {
    super(owner, args)
    this.eventTracker.track('begin_checkout')
  }

  get isKlarnaSelected() {
    return this.selectedPaymentMethodType === KLARNA_PAYMENT_METHOD_TYPE
  }

  @action
  async onCompletePurchase() {
    if (!this.canCompletePurchase) return

    try {
      if (this.isKlarnaSelected) {
        await this.payWithKlarna()
      } else {
        const orderInput = this.buildOrderInput()
        await this.createOrdersFromQuotes(orderInput)
      }
      this.flowManager.forward({ reevaluate: true })
    } catch (error) {
      Logger.error('Payment submission error:', { error })
    } finally {
      this.setPaymentFormLoading(false)
    }
  }

  @action
  async onEditBookingClicked() {
    this.flowManager.goToPage('FlowConfigBookingStartPage')
  }

  @action
  async onEditCustomerInfoClicked() {
    this.flowManager.goToPage('FlowConfigBookingInfoPage')
  }

  @action
  onDisabledButtonClick() {
    // guard against iOS ghost clicks: synthetic click events fired ~300ms after
    // a tap on the previous page can land on this button since it occupies the
    // same sticky-footer position. Ignore any click within 500ms of mount.
    if (Date.now() - this._constructedAt < 500) return

    if (this.canCompletePurchase) {
      PaymentAttemptIssue.dismiss()
      return
    }

    this.attemptedSubmitWithEmptyFields = true

    PaymentAttemptIssue.emit({ reason: 'missing-required-fields' })

    // Use later to ensure DOM updates and animations have completed
    later(this, () => this.scrollToPaymentSection(), 100)
  }

  scrollToPaymentSection() {
    const paymentSectionTop = document.getElementById('payment-section-top')
    if (!paymentSectionTop) return

    const scrollableContainer = paymentSectionTop.closest('[data-testid="flow-container"]')
    if (!scrollableContainer) return

    const containerRect = scrollableContainer.getBoundingClientRect()
    const elementRect = paymentSectionTop.getBoundingClientRect()
    const offsetFromContainerTop = elementRect.top - containerRect.top
    const currentScroll = scrollableContainer.scrollTop

    const themeHandler = IliadThemeHandler.getInstance()
    const headerOffset = themeHandler.getCssVariableValue('--page-header-scroll-offset', true) as number

    const targetScroll = currentScroll + offsetFromContainerTop - headerOffset

    scrollableContainer.scrollTo({
      top: Math.max(0, targetScroll),
      behavior: 'smooth'
    })
  }

  get shouldShowEmptyFieldsTooltip() {
    return this.attemptedSubmitWithEmptyFields && !this.canCompletePurchase
  }

  get showCartSummary() {
    return this.flowManager.allStepsValid
  }

  get canCompletePurchase() {
    return this.isFree || this.isKlarnaSelected || (this.paymentFormIsValid && Boolean(this.paymentMethod))
  }

  get isProcessingPayment() {
    return this.paymentFormIsLoading || this.isCreatingOrder
  }

  get paymentErrorMessageKey() {
    return (this.paymentErrorCode && PAYMENT_ERROR_MESSAGE_KEYS[this.paymentErrorCode]) || 'booking.payment.error.generic'
  }

  override onFlowUpdated(_activeSteps: FlowStep[], allStepsValid: boolean) {
    // When all steps are valid (payment is complete), automatically transition to confirmation
    if (allStepsValid && this.hasSuccessfulPayment) {
      this.flowManager.forward()
    }
  }

  override onExit() {
    // Closes a still-open Klarna tab and stops any in-flight capture listener.
    this.teardownKlarnaAttempt()
  }

  <template>
    <div data-testid='booking-flow' class='mx-auto my-0 flex h-full w-full flex-col items-center'>
      <ProgressBar @excludePages={{progressBarExcludedPages}} />

      <TwoColumn @stickyColumn='right'>
        <:leftColumn>
          <div class='flex flex-col gap-4'>
            {{#if (and this.cardIssuerDiscountRedeemable (not this.isFree))}}
              <CardIssuerDiscount />
            {{/if}}

            <StepContainer />

            {{! Mobile only: Your Order section after payment form }}
            <il-section
              header={{t 'booking.cart-summary.header'}}
              class='@sm/flow:il-section:rounded-3 @md/flow:hidden'
            >
              <div slot='content' class='flex flex-col gap-4'>
                <CartSummary @showTaxesAndFees={{true}} @isPaymentPage={{true}} @isMobile={{true}}>
                  <:orderActions>
                    {{! @glint-ignore: wait until we can't declare custom elements in glint }}
                    <il-button variant='ghost' small icon-only color='gray' onClick={{this.onEditBookingClicked}}>
                      <Icon @src='edit' />
                    </il-button>
                  </:orderActions>
                </CartSummary>

                <CustomerInfoSummary>
                  <:customerActions>
                    {{! @glint-ignore: wait until we can't declare custom elements in glint }}
                    <il-button variant='ghost' small icon-only color='gray' onClick={{this.onEditCustomerInfoClicked}}>
                      <Icon @src='edit' />
                    </il-button>
                  </:customerActions>
                </CustomerInfoSummary>
              </div>
            </il-section>
          </div>
        </:leftColumn>

        <:rightColumn>
          <HeroContainer />
        </:rightColumn>

        <:staticFooter>
          <StaticFooter @text={{t 'booking.footer.text' htmlSafe=true}} />
        </:staticFooter>

        <:stickyFooter>
          <StickyFooter>
            <div class='relative w-full'>
              {{! Error card - positioned absolutely above footer with animation }}
              <div class='absolute right-0 bottom-full left-0 z-100 pb-8'>
                <AnimatedContainer>
                  {{#animatedIf this.paymentErrorCode duration=300 use=this.animateHeight}}
                    <il-card type='warning' class='w-full' data-testid='payment-error-card'>
                      <div slot='section' class='flex items-center gap-2'>
                        <Icon @src='alert' />
                        <span>
                          {{t this.paymentErrorMessageKey}}
                        </span>
                      </div>
                    </il-card>
                  {{/animatedIf}}
                </AnimatedContainer>
              </div>

              {{#if (and (not this.canCompletePurchase) (not this.isProcessingPayment))}}
                <il-tooltip
                  {{! @glint-ignore: wait until we can't declare custom elements in glint }}
                  {{fixSafariTooltip}}
                  tooltip-text={{t 'booking.payment.incomplete-footer-tooltip'}}
                  tooltip-position='top'
                  fill
                >
                  {{! template-lint-disable no-invalid-interactive }}
                  <div {{on 'click' this.onDisabledButtonClick}} class='cursor-pointer'>
                    <div class='relative'>
                      <il-button fill disabled data-testid='payment-flow__complete-purchase-cta'>
                        {{t 'booking.payment.complete-purchase'}}
                      </il-button>
                      {{! Invisible overlay to capture clicks on disabled button }}
                      <div class='absolute inset-0 cursor-pointer'></div>
                    </div>
                  </div>
                </il-tooltip>
              {{else}}
                <il-button
                  fill
                  disabled={{or (not this.canCompletePurchase) this.isProcessingPayment}}
                  is-loading={{this.isProcessingPayment}}
                  {{! @glint-ignore: wait until we can't declare custom elements in glint }}
                  onClick={{this.onCompletePurchase}}
                  data-testid='payment-flow__complete-purchase-cta'
                >
                  {{t 'booking.payment.complete-purchase'}}
                </il-button>
              {{/if}}
            </div>
          </StickyFooter>
        </:stickyFooter>
      </TwoColumn>
    </div>
  </template>
}

export default RouteTemplate(BookingPaymentTemplate)
